import {
  ContractStatus,
  MilestoneStatus,
  AmendmentType,
  AmendmentStatus,
  Contract,
  Milestone,
  Amendment,
  ContractProgress,
  StatusBadgeConfig,
} from '../types/contract';

// Status Badge Configurations
export const contractStatusConfig: Record<ContractStatus, StatusBadgeConfig> = {
  PENDING_CLIENT_APPROVAL: {
    label: '待签署',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
    icon: 'clock',
  },
  DRAFT: {
    label: '草稿',
    color: '#9CA3AF',
    bgColor: '#F3F4F6',
    icon: 'file-text'
  },
  ACTIVE: {
    label: '进行中',
    color: '#2B5FFF',
    bgColor: '#DBEAFE',
    icon: 'play',
  },
  COMPLETED: {
    label: '已完成',
    color: '#00BFA6',
    bgColor: '#D1FAE5',
    icon: 'check',
  },
  CANCELLED: {
    label: '已取消',
    color: '#F44336',
    bgColor: '#FEE2E2',
    icon: 'x',
  },
};

export const milestoneStatusConfig: Record<MilestoneStatus, StatusBadgeConfig> = {
  NOT_STARTED: {
    label: '未开始',
    color: '#9CA3AF',
    bgColor: '#F3F4F6',
    icon: 'circle',
  },
  IN_PROGRESS: {
    label: '进行中',
    color: '#42A5F5',
    bgColor: '#E3F2FD',
    icon: 'play-circle',
  },
  SUBMITTED: {
    label: '已提交',
    color: '#9C27B0',
    bgColor: '#F3E5F5',
    icon: 'upload',
  },
  REVISION_REQUESTED: {
    label: '需要修改',
    color: '#FF9800',
    bgColor: '#FFF3E0',
    icon: 'refresh-cw',
  },
  COMPLETED: {
    label: '已完成',
    color: '#00BFA6',
    bgColor: '#D1FAE5',
    icon: 'check-circle',
  },
  CANCELLED: {
    label: '已取消',
    color: '#F44336',
    bgColor: '#FEE2E2',
    icon: 'x-circle',
  },
};

export const amendmentTypeConfig: Record<AmendmentType, { label: string; icon: string }> = {
  EXTEND_DEADLINE: {
    label: '延长截止日期',
    icon: 'calendar',
  },
  UPDATE_PAYMENT: {
    label: '修改付款金额',
    icon: 'dollar-sign',
  },
  ADD_MILESTONE: {
    label: '添加里程碑',
    icon: 'plus-circle',
  },
  MODIFY_MILESTONE: {
    label: '修改里程碑',
    icon: 'edit',
  },
  REMOVE_MILESTONE: {
    label: '删除里程碑',
    icon: 'trash-2',
  },
};

export const amendmentStatusConfig: Record<AmendmentStatus, StatusBadgeConfig> = {
  PENDING: {
    label: '待审核',
    color: '#F59E0B',
    bgColor: '#FEF3C7',
  },
  APPROVED: {
    label: '已批准',
    color: '#00BFA6',
    bgColor: '#D1FAE5',
  },
  REJECTED: {
    label: '已拒绝',
    color: '#F44336',
    bgColor: '#FEE2E2',
  },
};

// Permission Helpers
// Accept/Reject are deprecated — signing is done via e签宝 flow instead
export const canAcceptContract = (_contract: Contract, _userId: string): boolean => {
  return false;
};

export const canRejectContract = (_contract: Contract, _userId: string): boolean => {
  return false;
};

export const canCancelContract = (contract: Contract, userId: string): boolean => {
  return (
    (contract.status === 'PENDING_CLIENT_APPROVAL' || contract.status === 'ACTIVE') &&
    (contract.client_user_id === userId || contract.creator_user_id === userId)
  );
};

export const canEditContract = (contract: Contract, userId: string): boolean => {
  return (
    (contract.status === 'ACTIVE' || contract.status === 'DRAFT' || contract.status === 'PENDING_CLIENT_APPROVAL') &&
    contract.creator_user_id === userId
  );
};

export const canProposeContractEdit = (contract: Contract, userId: string): boolean => {
  return (
    contract.status === 'ACTIVE' &&
    contract.client_user_id === userId
  );
};

export const canStartMilestone = (
  milestone: Milestone,
  contract: Contract,
  userId: string
): boolean => {
  return (
    milestone.status === 'NOT_STARTED' &&
    contract.status === 'ACTIVE' &&
    contract.creator_user_id === userId
  );
};

export const canSubmitDeliverable = (
  milestone: Milestone,
  contract: Contract,
  userId: string
): boolean => {
  return (
    (milestone.status === 'IN_PROGRESS' ||
      milestone.status === 'REVISION_REQUESTED' ||
      milestone.status === 'SUBMITTED') &&
    contract.status === 'ACTIVE' &&
    contract.creator_user_id === userId
  );
};

export const canApproveMilestone = (
  milestone: Milestone,
  contract: Contract,
  userId: string
): boolean => {
  return (
    milestone.status === 'SUBMITTED' &&
    contract.status === 'ACTIVE' &&
    contract.client_user_id === userId
  );
};

export const canRequestRevision = (
  milestone: Milestone,
  contract: Contract,
  userId: string
): boolean => {
  return (
    milestone.status === 'SUBMITTED' &&
    contract.status === 'ACTIVE' &&
    contract.client_user_id === userId
  );
};

export const canCancelMilestone = (
  milestone: Milestone,
  contract: Contract,
  userId: string
): boolean => {
  return (
    milestone.status !== 'COMPLETED' &&
    milestone.status !== 'CANCELLED' &&
    contract.status === 'ACTIVE' &&
    contract.client_user_id === userId
  );
};

export const canProposeAmendment = (contract: Contract, userId: string): boolean => {
  return (
    contract.status === 'ACTIVE' &&
    (contract.client_user_id === userId || contract.creator_user_id === userId)
  );
};

export const canReviewAmendment = (
  amendment: Amendment,
  contract: Contract,
  userId: string
): boolean => {
  return (
    amendment.status === 'PENDING' &&
    amendment.proposed_by !== userId &&
    (contract.client_user_id === userId || contract.creator_user_id === userId)
  );
};

// Progress Calculation
export const calculateContractProgress = (milestones: Milestone[]): ContractProgress => {
  const totalMilestones = milestones.length;
  const completedMilestones = milestones.filter((m) => m.status === 'COMPLETED').length;
  const inProgressMilestones = milestones.filter((m) => m.status === 'IN_PROGRESS').length;
  const notStartedMilestones = milestones.filter((m) => m.status === 'NOT_STARTED').length;
  const cancelledMilestones = milestones.filter((m) => m.status === 'CANCELLED').length;

  const totalPaid = milestones
    .filter((m) => m.status === 'COMPLETED')
    .reduce((sum, m) => sum + m.payment_amount, 0);

  const totalPending = milestones
    .filter((m) => m.status !== 'COMPLETED' && m.status !== 'CANCELLED')
    .reduce((sum, m) => sum + m.payment_amount, 0);

  const activeMilestones = totalMilestones - cancelledMilestones;
  const progressPercentage = activeMilestones > 0
    ? Math.round((completedMilestones / activeMilestones) * 100)
    : 0;

  return {
    totalMilestones,
    completedMilestones,
    inProgressMilestones,
    notStartedMilestones,
    cancelledMilestones,
    totalPaid,
    totalPending,
    progressPercentage,
  };
};

// Date Formatting Helpers
export const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return formatDate(timestamp);
  } else if (days > 0) {
    return `${days}天前`;
  } else if (hours > 0) {
    return `${hours}小时前`;
  } else if (minutes > 0) {
    return `${minutes}分钟前`;
  } else {
    return '刚刚';
  }
};

export const getDaysUntil = (timestamp: number): number => {
  const now = Date.now();
  const diff = timestamp - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const isOverdue = (timestamp: number): boolean => {
  return Date.now() > timestamp;
};

// Currency Formatting
export const formatCurrency = (amount: number, currency: string = 'CNY'): string => {
  const currencySymbols: Record<string, string> = {
    CNY: '¥',
    USD: '$',
    EUR: '€',
  };

  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// User Role Helper
export const getUserRole = (contract: Contract, userId: string): 'client' | 'creator' | null => {
  if (contract.client_user_id === userId) return 'client';
  if (contract.creator_user_id === userId) return 'creator';
  return null;
};

// Contract Status Helpers
export const isContractActive = (contract: Contract): boolean => {
  return contract.status === 'ACTIVE';
};

export const isContractPending = (contract: Contract): boolean => {
  return contract.status === 'PENDING_CLIENT_APPROVAL';
};

export const isContractCompleted = (contract: Contract): boolean => {
  return contract.status === 'COMPLETED';
};

export const isContractCancelled = (contract: Contract): boolean => {
  return contract.status === 'CANCELLED';
};

// Milestone Status Helpers
export const isMilestoneCompleted = (milestone: Milestone): boolean => {
  return milestone.status === 'COMPLETED';
};

export const isMilestoneSubmitted = (milestone: Milestone): boolean => {
  return milestone.status === 'SUBMITTED';
};

export const isMilestoneRevisionRequested = (milestone: Milestone): boolean => {
  return milestone.status === 'REVISION_REQUESTED';
};

// Sort milestones by order_index
export const sortMilestones = (milestones: Milestone[]): Milestone[] => {
  return [...milestones].sort((a, b) => a.order_index - b.order_index);
};
